import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { FormHeading, Well } from '../../../form';
import BadgeList from './BadgeList';
import { useDataFetcher } from '../../../hooks';
import { IBadgeListItem } from './types';
import AjaxErrorBoundary from '../../../ajax/errorBoundary';
import ErrorList from '../../../errorList';
import {
  ConfirmationModal,
  IConfirmationModalProps,
  useModal,
} from '../../../modals';
import deactivateBadge from './deactivateBadge';
import activateBadge from './activateBadge';
import { IApiError } from '../../../../services';
import { ButtonContainer } from './BadgeListPage.styled';

const config = {
  transformer: (data: any): Array<IBadgeListItem> =>
    data.badges.map((item: IBadgeListItem) => transformItem(item)),
};

const transformItem = (item: IBadgeListItem): IBadgeListItem => {
  const badge = { ...item };

  if (badge.lastVisitor) {
    badge.lastVisitor.dated = new Date(
      badge.lastVisitor.dated,
    ).toLocaleDateString('en-GB');
  }
  return badge;
};

const BadgeListPage: React.FC = () => {
  const history = useHistory();
  const { showModal, hideModal } = useModal();

  const { busy, errors, data } = useDataFetcher('/api/badges', config);

  const [badges, setBadges] = useState<Array<IBadgeListItem>>([]);
  const [showDeactivated, setShowDeactivated] = useState<boolean>(false);
  const [deactivatedCount, setDeactivatedCount] = useState<number>(0);

  const [ajaxErrors, setAjaxErrors] = useState<Array<string>>([]);

  useEffect(() => {
    if (data) {
      setBadges(data);
    }
  }, [data]);

  useEffect(() => {
    setDeactivatedCount(
      badges.filter((badge: IBadgeListItem) => badge.deactivated).length,
    );
  }, [badges]);

  const getFilteredBadges = (
    badges: Array<IBadgeListItem>,
    showDeactivated: boolean,
  ): Array<IBadgeListItem> =>
    showDeactivated
      ? badges
      : badges.filter((badge: IBadgeListItem) => !badge.deactivated);

  const filteredBadges = useMemo(
    () => getFilteredBadges(badges, showDeactivated),
    [badges, showDeactivated],
  );

  const updateDeactivatedFlag = (badgeNumber: string, deactivated: boolean) => {
    const index = badges.findIndex((badge) => badge.number === badgeNumber);

    setBadges([
      ...badges.slice(0, index),
      { ...badges[index], deactivated },
      ...badges.slice(index + 1),
    ]);
  };

  const handleClickRegister = (): void =>
    history.replace('/reception/badges/register');

  const handleDeactivateBadge = (badgeNumber: string): void => {
    const confirmationProps: IConfirmationModalProps = {
      title: 'Deactivate Badge',
      message: 'Are you sure that you to deactivate this badge?',
      icon: ['far', 'question-circle'],
      kind: 'danger',
      onConfirmed: () => {
        hideModal();

        deactivateBadge(badgeNumber)
          .then(() => {
            updateDeactivatedFlag(badgeNumber, true);
          })
          .catch((error: IApiError) => setAjaxErrors(error.messages));
      },
      onRequestClose: () => hideModal(),
    };

    showModal(ConfirmationModal, confirmationProps);
  };

  const handleActivateBadge = (badgeNumber: string): void => {
    const confirmationProps: IConfirmationModalProps = {
      title: 'Activate Badge',
      message: 'Are you sure that you to activate this badge?',
      icon: ['far', 'question-circle'],
      kind: 'info',
      onConfirmed: () => {
        hideModal();

        activateBadge(badgeNumber)
          .then(() => {
            updateDeactivatedFlag(badgeNumber, false);
          })
          .catch((error: IApiError) => setAjaxErrors(error.messages));
      },
      onRequestClose: () => hideModal(),
    };

    showModal(ConfirmationModal, confirmationProps);
  };

  const toggleShowDeactivated = (): void => {
    setShowDeactivated(!showDeactivated);
  };

  return (
    <Container>
      <Well>
        <FormHeading title="Badges" backPath="/reception" />
        <ButtonContainer>
          <Button type="button" onClick={handleClickRegister}>
            Register new badge
          </Button>
          <Button type="button" variant="info" onClick={toggleShowDeactivated}>
            {`${
              showDeactivated ? 'Hide' : 'Show'
            } Deactivated (${deactivatedCount})`}
          </Button>
        </ButtonContainer>
        <AjaxErrorBoundary
          busy={busy}
          loadingText="Loading badges"
          errors={errors}
        >
          <ErrorList errors={ajaxErrors} />
          <BadgeList
            badges={filteredBadges}
            onDeactivate={handleDeactivateBadge}
            onActivate={handleActivateBadge}
          />
        </AjaxErrorBoundary>
      </Well>
    </Container>
  );
};

export default BadgeListPage;
